import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  Subscription,
  SubscriptionResponse,
  SubscriptionPaymentsResponse,
  ChangePlanRequest,
  ChangePlanResponse,
  RenewSubscriptionRequest,
  RenewSubscriptionResponse,
  CancelSubscriptionResponse,
  SuperAdminStats,
  SuperAdminBusiness,
  SuperAdminOwner,
  SubscriptionPayment,
  PendingOwnerSignup,
} from '../models/sala.model';

/** Cuenta de usuario tal como la gestiona Super Admin › Usuarios. */
export interface AdminUserAccount {
  user_id: number;
  name: string;
  email: string;
  phone_number?: string | null;
  role: 'customer' | 'owner' | 'employee' | 'super_admin';
  is_active: boolean;
  force_password_change?: boolean;
  created_at?: string;
  last_login_at?: string | null;
}

export interface AdminUserBusiness {
  id: number;
  name: string;
  company?: string;
  status: boolean;
  is_archived?: boolean;
  relation: 'owner' | 'delegated_owner' | 'employee';
}

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {

  private readonly SAAS_URL = `${environment.apiUrl}/saas`;

  constructor(private http: HttpClient) {}

  // ---------------------------------------------------------------------------
  // Owner: Gestión de suscripción propia
  // ---------------------------------------------------------------------------

  /** Obtener la suscripción activa del owner autenticado */
  getSubscription(): Observable<SubscriptionResponse> {
    return this.http.get<SubscriptionResponse>(`${this.SAAS_URL}/subscription`);
  }

  /** Cambiar el plan de suscripción */
  changePlan(new_plan: string): Observable<ChangePlanResponse> {
    const body: ChangePlanRequest = { new_plan: new_plan as any };
    return this.http.post<ChangePlanResponse>(`${this.SAAS_URL}/subscription/change-plan`, body);
  }

  /** Renovar la suscripción usando un payment_id aprobado */
  renewSubscription(payment_id: number): Observable<RenewSubscriptionResponse> {
    const body: RenewSubscriptionRequest = { payment_id };
    return this.http.post<RenewSubscriptionResponse>(`${this.SAAS_URL}/subscription/renew`, body);
  }

  /** Cancelar la suscripción */
  cancelSubscription(current_password: string, reason: string): Observable<CancelSubscriptionResponse> {
    return this.http.post<CancelSubscriptionResponse>(`${this.SAAS_URL}/subscription/cancel`, { current_password, reason });
  }

  /** Historial de pagos de suscripción */
  getPaymentHistory(page = 1, limit = 25): Observable<SubscriptionPaymentsResponse> {
    return this.http.get<SubscriptionPaymentsResponse>(`${this.SAAS_URL}/subscription/payments`, { params: { page, limit } });
  }

  downloadOwnSubscriptionReceipt(paymentId: number): Observable<Blob> {
    return this.http.get(`${this.SAAS_URL}/subscription/payments/${paymentId}/receipt`, { responseType: 'blob' });
  }

  // ---------------------------------------------------------------------------
  // Super Admin: Estadísticas y gestión global
  // ---------------------------------------------------------------------------

  /** Estadísticas generales de la plataforma */
  getAdminStats(includeMonthly = false, from = '', to = ''): Observable<SuperAdminStats> {
    const period = `${from ? `&from=${encodeURIComponent(from)}` : ''}${to ? `&to=${encodeURIComponent(to)}` : ''}`;
    const suffix = `?refresh=${Date.now()}${includeMonthly ? '&include_monthly=true' : ''}${period}`;
    return this.http.get<SuperAdminStats>(`${this.SAAS_URL}/admin/stats${suffix}`);
  }

  /** Lista todos los negocios */
  getAllBusinesses(page = 1, limit = 25, search = '', archived = false): Observable<{ total: number; page: number; pages: number; businesses: SuperAdminBusiness[] }> {
    return this.http.get<any>(`${this.SAAS_URL}/admin/businesses?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}&archived=${archived}`);
  }

  /** Lista todos los owners */
  getAllOwners(page = 1, limit = 25, search = ''): Observable<{ total: number; page: number; pages: number; owners: SuperAdminOwner[] }> {
    return this.http.get<any>(`${this.SAAS_URL}/admin/owners?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`);
  }

  /** Lista todas las suscripciones */
  getAllSubscriptions(page = 1, limit = 25, status?: string, plan?: string, search = ''): Observable<{ total: number; page: number; pages: number; subscriptions: any[] }> {
    const filter = `${status ? `&status=${status}` : ''}${plan ? `&plan=${plan}` : ''}&search=${encodeURIComponent(search)}`;
    return this.http.get<any>(`${this.SAAS_URL}/admin/subscriptions?page=${page}&limit=${limit}${filter}`);
  }

  /** Lista todos los pagos */
  getAllPayments(page = 1, limit = 25, type?: string, search = '', status?: string): Observable<{ total: number; page: number; pages: number; totalAmount: number; paymentSummary: Record<string, { count: number; amount: number }>; payments: SubscriptionPayment[] }> {
    const filter = `${type ? `&type=${type}` : ''}${status ? `&status=${status}` : ''}&search=${encodeURIComponent(search)}`;
    return this.http.get<any>(`${this.SAAS_URL}/admin/payments?page=${page}&limit=${limit}${filter}`);
  }

  downloadAdminSubscriptionReceipt(paymentId: number): Observable<Blob> {
    return this.http.get(`${this.SAAS_URL}/admin/payments/${paymentId}/receipt`, { responseType: 'blob' });
  }

  /** Lista todos los usuarios */
  getAllUsers(page = 1, limit = 25, role?: string, search = ''): Observable<{ total: number; page: number; pages: number; users: any[] }> {
    const filter = `${role ? `&role=${role}` : ''}&search=${encodeURIComponent(search)}`;
    return this.http.get<any>(`${this.SAAS_URL}/admin/users?page=${page}&limit=${limit}${filter}`);
  }

  resetUserPassword(user_id: number, frontendBaseUrl?: string): Observable<{ msg: string }> {
    return this.http.patch<{ msg: string }>(
      `${this.SAAS_URL}/admin/users/${user_id}/password`,
      frontendBaseUrl ? { frontend_base_url: frontendBaseUrl } : {}
    );
  }

  updateUserStatus(user_id: number, is_active: boolean): Observable<{ msg: string; user: any }> {
    return this.http.patch<{ msg: string; user: any }>(`${this.SAAS_URL}/admin/users/${user_id}/status`, { is_active });
  }

  /** Ficha completa de una cuenta (datos y negocios vinculados). */
  getAdminUser(user_id: number): Observable<{ user: AdminUserAccount; businesses: AdminUserBusiness[] }> {
    return this.http.get<{ user: AdminUserAccount; businesses: AdminUserBusiness[] }>(`${this.SAAS_URL}/admin/users/${user_id}`);
  }

  updateAdminUser(user_id: number, payload: { name?: string; email?: string; phone_number?: string }): Observable<{ msg: string; user: AdminUserAccount }> {
    return this.http.patch<{ msg: string; user: AdminUserAccount }>(`${this.SAAS_URL}/admin/users/${user_id}`, payload);
  }

  /** Asigna manualmente una contraseña nueva (cierra todas sus sesiones). */
  setAdminUserPassword(user_id: number, payload: { password: string; force_change: boolean; notify: boolean }): Observable<{ msg: string; email_warning: boolean }> {
    return this.http.put<{ msg: string; email_warning: boolean }>(`${this.SAAS_URL}/admin/users/${user_id}/password`, payload);
  }

  revokeAdminUserSessions(user_id: number): Observable<{ msg: string }> {
    return this.http.post<{ msg: string }>(`${this.SAAS_URL}/admin/users/${user_id}/sessions/revoke`, {});
  }

  updateBusinessStatus(business_id: number, status: boolean): Observable<{ msg: string; business: any }> {
    return this.http.patch<{ msg: string; business: any }>(`${this.SAAS_URL}/admin/businesses/${business_id}/status`, { status });
  }

  archiveBusiness(business_id: number, password: string): Observable<{ msg: string; business: any; notification_warning?: boolean }> {
    return this.http.post<{ msg: string; business: any; notification_warning?: boolean }>(`${this.SAAS_URL}/admin/businesses/${business_id}/archive`, { password });
  }

  restoreBusiness(business_id: number): Observable<{ msg: string; business: any; notification_warning?: boolean }> {
    return this.http.post<{ msg: string; business: any; notification_warning?: boolean }>(`${this.SAAS_URL}/admin/businesses/${business_id}/restore`, {});
  }

  confirmManualSubscriptionPayment(subscription_id: number, payload: { payment_method: string; payment_reference?: string }): Observable<{ msg: string; subscription: any; payment: any }> {
    return this.http.post<{ msg: string; subscription: any; payment: any }>(`${this.SAAS_URL}/admin/subscriptions/${subscription_id}/confirm-payment`, payload);
  }

  getSubscriptionCollectionLink(subscription_id: number): Observable<{ paymentLinkUrl: string }> {
    return this.http.get<{ paymentLinkUrl: string }>(`${this.SAAS_URL}/admin/subscriptions/${subscription_id}/collection-link`);
  }

  sendSubscriptionCollectionLinkEmail(subscription_id: number): Observable<{ msg: string }> {
    return this.http.post<{ msg: string }>(`${this.SAAS_URL}/admin/subscriptions/${subscription_id}/send-collection-link-email`, {});
  }

  exportPlatformAnalytics(): Observable<Blob> {
    return this.http.get(`${this.SAAS_URL}/admin/analytics/export`, { responseType: 'blob' });
  }

  exportPlatformAnalyticsPdf(month = ''): Observable<Blob> {
    const suffix = month ? `?month=${encodeURIComponent(month)}` : '';
    return this.http.get(`${this.SAAS_URL}/admin/analytics/export/pdf${suffix}`, { responseType: 'blob' });
  }

  createManualUser(payload: { name: string; email: string; phone_number?: string; role: 'owner' | 'super_admin'; business_name?: string; business_phone?: string; plan?: string; external_payment?: { method: string; reference?: string; receipts?: { file_name: string; original_name?: string; file_type?: string; mime?: string }[] } }): Observable<{ msg: string; status?: 'pending_payment' | 'active'; email_warning: boolean }> {
    return this.http.post<{ msg: string; status?: 'pending_payment' | 'active'; email_warning: boolean }>(`${this.SAAS_URL}/admin/users/manual`, payload);
  }

  // Comprobante del pago externo (efectivo, transferencia, etc.) de un alta
  // manual de propietario -- mismo patrón que subirComprobante() de reservas
  // (reservation.service.ts): sube el archivo de inmediato y devuelve su
  // referencia, que luego se envía dentro de external_payment.receipts al
  // crear la cuenta.
  uploadManualUserReceipt(archivo: File): Observable<{ file_name: string; public_url: string; original_name: string; file_type: string; mime: string }> {
    const formData = new FormData();
    formData.append('comprobante', archivo, archivo.name);
    return this.http.post<{ file_name: string; public_url: string; original_name: string; file_type: string; mime: string }>(
      `${this.SAAS_URL}/admin/users/manual/receipt/upload`,
      formData
    );
  }

  // Cuentas de propietario dadas de alta manualmente con plan de pago que
  // todavía no completan su pago — para mostrarlas en el panel y poder
  // reenviar el enlace de activación, editarlas o confirmarlas por pago externo.
  listPendingOwnerSignups(): Observable<{ items: PendingOwnerSignup[] }> {
    return this.http.get<{ items: PendingOwnerSignup[] }>(`${this.SAAS_URL}/admin/owners-pending`);
  }

  resendOwnerPaymentLink(pendingId: number): Observable<{ msg: string }> {
    return this.http.post<{ msg: string }>(`${this.SAAS_URL}/admin/owners-pending/${pendingId}/resend-payment-link`, {});
  }

  getPendingOwnerPaymentLink(pendingId: number): Observable<{ paymentLinkUrl: string }> {
    return this.http.get<{ paymentLinkUrl: string }>(`${this.SAAS_URL}/admin/owners-pending/${pendingId}/payment-link`);
  }

  // Corrección pedida: antes la única acción sobre una cuenta pendiente era
  // reenviar/copiar el enlace -- ahora también se puede corregir sus datos
  // o confirmarla directamente si el cliente pagó por otro medio.
  editPendingOwnerSignup(pendingId: number, payload: { full_name: string; company: string; email: string; business_email?: string; phone?: string; plan: string; custom_price_monthly?: number | ''; commission_rate?: number | ''; grace_days?: number | '' }): Observable<{ msg: string; item: PendingOwnerSignup }> {
    return this.http.patch<{ msg: string; item: PendingOwnerSignup }>(`${this.SAAS_URL}/admin/owners-pending/${pendingId}`, payload);
  }

  confirmPendingOwnerExternalPayment(pendingId: number, payload: { external_payment: { method: string; reference?: string; receipts?: { file_name: string; original_name?: string; file_type?: string; mime?: string }[] } }): Observable<{ msg: string; status: string; email_warning: boolean }> {
    return this.http.post<{ msg: string; status: string; email_warning: boolean }>(`${this.SAAS_URL}/admin/owners-pending/${pendingId}/confirm-external-payment`, payload);
  }

  getAdminPlans(): Observable<{ plans: any[] }> {
    return this.http.get<{ plans: any[] }>(`${this.SAAS_URL}/admin/plans`);
  }

  getSubscriptionContractHistory(subscriptionId: number): Observable<{ history: any[] }> {
    return this.http.get<{ history: any[] }>(`${this.SAAS_URL}/admin/subscriptions/${subscriptionId}/contract-history`);
  }

  updatePlan(planId: number, plan: any): Observable<{ plan: any }> {
    return this.http.put<{ plan: any }>(`${this.SAAS_URL}/admin/plans/${planId}`, plan);
  }

  getPlanHistory(planId: number): Observable<{ history: any[] }> {
    return this.http.get<{ history: any[] }>(`${this.SAAS_URL}/admin/plans/${planId}/history`);
  }

  getPublicPlans(): Observable<{ plans: any[] }> {
    return this.http.get<{ plans: any[] }>(`${this.SAAS_URL}/plans`);
  }

  createPlanChangePayment(payload: { monto: number; card_number: string; cvv: string; exp_date: string; cardholderName: string; product_api: string; description: string; payment_reference: string; ambiente: '0' | '1' }): Observable<{ idLink: string; url?: string }> {
    return this.http.post<{ idLink: string; url?: string }>(`${environment.apiUrl}/payments/apay/transactor`, payload);
  }

  confirmPlanChange(new_plan: string, transaction_id: string): Observable<{ msg: string; payment_id: number }> {
    return this.http.post<{ msg: string; payment_id: number }>(`${this.SAAS_URL}/subscription/confirm-plan-change`, { new_plan, transaction_id });
  }

  confirmRenewalPayment(transaction_id: string, months: number): Observable<{ msg: string; payment_id: number }> {
    return this.http.post<{ msg: string; payment_id: number }>(`${this.SAAS_URL}/subscription/confirm-renewal`, { transaction_id, months });
  }

  confirmTrialUpgrade(new_plan: string, transaction_id: string): Observable<{ msg: string; payment_id: number }> {
    return this.http.post<{ msg: string; payment_id: number }>(`${this.SAAS_URL}/subscription/confirm-trial-upgrade`, { new_plan, transaction_id });
  }

  updateSubscriptionRenewalDate(subscriptionId: number, renewal_at: string): Observable<{ msg: string; subscription: Subscription }> {
    return this.http.patch<{ msg: string; subscription: Subscription }>(`${this.SAAS_URL}/admin/subscriptions/${subscriptionId}/renewal-date`, { renewal_at });
  }

  updateSubscriptionCustomization(subscriptionId: number, payload: { custom_max_spaces: number | null; custom_price_monthly: number | null; custom_pricing_notes: string | null; requested_by_user_id: number; new_plan?: string | null; apply_current_plan_price?: boolean; commission_rate?: number | null; grace_days?: number | null }): Observable<any> {
    return this.http.patch(`${this.SAAS_URL}/admin/subscriptions/${subscriptionId}/customization`, payload);
  }

  getSubscriptionBusinessUsers(subscriptionId: number): Observable<{ business: { id: number; name: string }; users: Array<{ user_id: number; name: string; email: string; role: 'owner' | 'employee'; role_label: string; is_active: boolean }> }> {
    return this.http.get<any>(`${this.SAAS_URL}/admin/subscriptions/${subscriptionId}/business-users`);
  }

  getPaymentStatus(transactionId: string): Observable<{ status: 'pending' | 'succeeded' | 'failed' }> {
    return this.http.get<{ status: 'pending' | 'succeeded' | 'failed' }>(`${environment.apiUrl}/payments/get-payment/${encodeURIComponent(transactionId)}`);
  }

  // ---------------------------------------------------------------------------
  // Asistente de conversión de cuenta de prueba → definitiva (dentro del panel)
  // ---------------------------------------------------------------------------

  getTrialProfile(): Observable<{ profile: { name: string; company: string; email: string; phone: string } }> {
    return this.http.get<any>(`${this.SAAS_URL}/subscription/trial-profile`);
  }

  updateTrialProfile(payload: { name: string; company: string; email: string; phone: string }): Observable<{ msg: string; email_changed: boolean; profile: { name: string; company: string; email: string; phone: string } }> {
    return this.http.patch<any>(`${this.SAAS_URL}/subscription/trial-profile`, payload);
  }

  sendEmailVerification(email: string, name: string): Observable<{ verificationId: string; expiresIn: number }> {
    return this.http.post<any>(`${this.SAAS_URL}/email-verification/send`, { email, name });
  }

  verifyEmailCode(verificationId: string, code: string): Observable<{ msg: string }> {
    return this.http.post<any>(`${this.SAAS_URL}/email-verification/verify`, { verificationId, code });
  }
}
